import ReactDOM from 'react-dom/client';
import './index.css';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './feat/api-service-layer/queryClient';
import reportWebVitals from './reportWebVitals';
import AppRouter from './routes/AppRouter';

// Suppress harmless errors from browser extensions, ad blockers, and Mapbox telemetry
if (typeof window !== 'undefined') {
  // Handle unhandled promise rejections (fetch errors)
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason;
    const errorString = typeof error === 'string' ? error : error?.message || String(error);

    // Check if it's a Mapbox events API error blocked by client
    if (errorString.includes('ERR_BLOCKED_BY_CLIENT') || errorString.includes('events.mapbox.com')) {
      event.preventDefault(); // Suppress the error
      return;
    }
  });

  // Helper function to check if an error should be suppressed
  const shouldSuppressError = (args: unknown[]) => {
    // Convert all arguments to strings for checking
    const errorStrings = args.map((arg) => {
      if (typeof arg === 'string') return arg;
      if (arg instanceof Error) return arg.message;
      if (arg && typeof arg === 'object' && 'toString' in arg) return String(arg);
      return String(arg);
    });
    const errorString = errorStrings.join(' ');

    // Check each argument individually (in case URL and error are separate)
    const hasChromeExtension = errorStrings.some((str) => str.includes('chrome-extension://'));
    const _hasFileNotFound = errorStrings.some(
      (str) => str.includes('ERR_FILE_NOT_FOUND') || str.includes('net::ERR_FILE_NOT_FOUND'),
    );

    // Mapbox events API errors blocked by ad blockers
    if (
      (errorString.includes('ERR_BLOCKED_BY_CLIENT') && errorString.includes('events.mapbox.com')) ||
      (errorString.includes('POST') &&
        errorString.includes('events.mapbox.com') &&
        errorString.includes('ERR_BLOCKED_BY_CLIENT'))
    ) {
      return true;
    }

    // Chrome extension errors (harmless, from browser extensions)
    // Suppress any error related to chrome extensions - they're always harmless
    if (hasChromeExtension || errorString.includes('chrome-extension://')) {
      return true;
    }

    // Mapbox terrain/hillshade warning (informational, not an error)
    if (errorString.includes('Terrain and hillshade are disabled because of Canvas2D limitations')) {
      return true;
    }

    return false;
  };

  // Suppress console errors
  const originalConsoleError = console.error;
  console.error = (...args) => {
    if (shouldSuppressError(args)) {
      return; // Suppress the console error
    }
    originalConsoleError.apply(console, args);
  };

  // Suppress console warnings for informational messages
  const originalConsoleWarn = console.warn;
  console.warn = (...args) => {
    if (shouldSuppressError(args)) {
      return; // Suppress the console warning
    }
    originalConsoleWarn.apply(console, args);
  };
}

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Root element not found');
const root = ReactDOM.createRoot(rootElement);
root.render(
  <QueryClientProvider client={queryClient}>
    <AppRouter />
  </QueryClientProvider>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
